import { PropsWithChildren, useLayoutEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader } from '../../components/Loader';
import { LOGIN_REQUIRED_CLIENTS } from '../../consts/authentication';
import { LOCAL_STORAGE_KEYS } from '../../consts/localStorage';
import { useShouldNavigateToOnboardingWizard } from '../../hooks/useShouldNavigateToOnboardingWizard';
import { useUser } from '../../hooks/useUser';
import { DEFAULT_STEP_INDEX } from '../../layouts/OnboardingWizard/consts';
import { buildStepUrl } from '../../layouts/OnboardingWizard/utils';
import { consumeAndRemoveRedirectToFromLocalStorage } from '../../utils/localStorage';
import { getQueryParam } from '../../utils/url';
import { QUERY_PARAMS, ROUTES } from '../consts';

export const RootProtectedRoute = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const { user, isLoading: isUserLoading } = useUser();
  const clientQueryParam = getQueryParam(QUERY_PARAMS.INIT_CLIENT) || 'portal';

  const {
    shouldNavigateToOnboardingWizard,
    isLoading: isShouldNavigateLoading,
  } = useShouldNavigateToOnboardingWizard();

  const isLoading = isUserLoading || isShouldNavigateLoading;

  const shouldRedirectToLogin =
    !user || LOGIN_REQUIRED_CLIENTS.includes(clientQueryParam as string);
  const resumeTarget = `${location.pathname}${location.search}`;

  useLayoutEffect(() => {
    if (isLoading || !shouldRedirectToLogin) {
      return;
    }

    localStorage.setItem(
      LOCAL_STORAGE_KEYS.REDIRECT_TO,
      JSON.stringify(resumeTarget),
    );
  }, [isLoading, resumeTarget, shouldRedirectToLogin]);

  if (isLoading) {
    return <Loader center />;
  }

  if (shouldRedirectToLogin) {
    return <Navigate to={`${ROUTES.SIGNIN}${location.search}`} replace />;
  }

  if (shouldNavigateToOnboardingWizard) {
    return <Navigate to={buildStepUrl(DEFAULT_STEP_INDEX)} />;
  }

  const pendingRedirect = consumeAndRemoveRedirectToFromLocalStorage();
  const shouldRedirectToRedirectToParamValue =
    pendingRedirect &&
    pendingRedirect !== `${location.pathname}${location.search}`;

  if (shouldRedirectToRedirectToParamValue) {
    return <Navigate to={pendingRedirect} replace />;
  }

  return <>{children}</>;
};
